/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, ViewEncapsulation, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent, NotificationService } from '@alfresco/adf-core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { PageStateComponent, ThaiDatePipe } from '@alfresco/aca-shared';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { forkJoin, interval, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { fileKindClass, fileKindIcon } from './my-tasks.service';
import { TemplateInstance, WorkflowItem, WorkflowProcess, WorkflowService, WorkflowStep, WorkflowTask } from './workflow.service';

type Tab = 'steps' | 'info' | 'docs' | 'history';

/**
 * Workflow tracking (WF-08): one process instance, end to end.
 *
 * Everything on the page is engine state — the process record, its tasks in
 * any state (the step timeline), the attached documents, and the process
 * variables the initiator supplied. When the instance came from a custom
 * template its expected step list overlays the timeline, so "waiting" steps
 * are visible before the engine has created their tasks.
 */
@Component({
  selector: 'app-workflow-instance',
  standalone: true,
  imports: [CommonModule, RouterModule, MatIconModule, MatButtonModule, MatProgressBarModule, PageStateComponent, ThaiDatePipe, TranslatePipe],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './instance-detail.component.html',
  styles: []
})
export class InstanceDetailComponent implements OnInit {
  private readonly wf = inject(WorkflowService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly notification = inject(NotificationService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  /** How often a running flow is read again while the page stays open. */
  static readonly REFRESH_MS = 30_000;

  readonly loading = signal(true);
  readonly cancelling = signal(false);
  /** Who is looking — the cancel button is the starter's. */
  readonly me = signal('');
  readonly proc = signal<WorkflowProcess | undefined>(undefined);
  readonly tasks = signal<WorkflowTask[]>([]);
  readonly items = signal<WorkflowItem[]>([]);
  readonly variables = signal<Record<string, any>>({});
  readonly tab = signal<Tab>('steps');

  /**
   * The template flow this process is a step of, read from the jobs sidecar.
   * It spans every step's process, so the page tells the whole story even
   * when it was opened from the first one.
   */
  readonly instance = signal<TemplateInstance | null>(null);

  readonly activeTask = computed(() => this.tasks().find((t) => t.state !== 'completed'));

  /** The template's expected steps, overlaid on real task state. */
  readonly plannedSteps = computed(() => {
    const inst = this.instance();
    const tpl = inst ? this.wf.getTemplates().find((t) => t.id === inst.templateId) : undefined;
    return tpl?.steps ?? [];
  });

  /**
   * One step-table row per real task, framed by synthesized start/end nodes the
   * way the tracking artwork draws them; template steps ahead show as waiting.
   * `note` carries the recorded outcome for rows a template flow completed.
   */
  readonly timeline = computed(() => {
    interface StepRow {
      name: string;
      desc: string;
      who: string;
      at?: string;
      state: 'done' | 'active' | 'waiting';
      note: string;
    }
    const p = this.proc();
    const inst = this.instance();
    const planned = this.plannedSteps();
    if (inst && planned.length) {
      return this.flowTimeline(inst, planned);
    }
    const rows: StepRow[] = [
      {
        name: 'DMS.INST.START_NODE',
        desc: 'DMS.INST.START_DESC',
        who: p?.startUserId ?? '',
        at: p?.startedAt,
        state: 'done',
        note: 'DMS.INST.OC_COMPLETED'
      }
    ];
    const tasks = [...this.tasks()].sort((a, b) => String(a.startedAt).localeCompare(String(b.startedAt)));
    tasks.forEach((t, i) => {
      const outcome = inst?.history.find((h) => h.step === i)?.outcome;
      rows.push({
        name: t.name,
        desc: t.description ?? '',
        who: t.assignee ?? '',
        at: t.endedAt ?? t.startedAt,
        state: t.state === 'completed' ? 'done' : 'active',
        note: outcome === 'Approve' ? 'DMS.INST.OC_APPROVED' : outcome === 'Reject' ? 'DMS.INST.OC_REJECTED' : outcome ? 'DMS.INST.OC_COMPLETED' : ''
      });
    });
    for (let i = tasks.length; i < planned.length; i++) {
      rows.push({
        name: planned[i].name,
        desc: '',
        who: planned[i].assigneeLabel || planned[i].assignee || '',
        at: undefined,
        state: 'waiting',
        note: ''
      });
    }
    rows.push({
      name: 'DMS.INST.END_NODE',
      desc: 'DMS.INST.END_DESC',
      who: '—',
      at: p?.endedAt,
      state: p?.completed ? 'done' : 'waiting',
      note: ''
    });
    return rows;
  });

  /**
   * A template flow told from its record: each decision as it was taken, the
   * step waiting now, and the steps still to come — across all of its
   * processes, not only the one this page was opened on.
   */
  private flowTimeline(inst: TemplateInstance, planned: WorkflowStep[]) {
    const note = (outcome: string) =>
      outcome === 'Approve' ? 'DMS.INST.OC_APPROVED' : outcome === 'Reject' ? 'DMS.INST.OC_REJECTED' : 'DMS.INST.OC_COMPLETED';
    const running = inst.status === 'running';
    const rows = [
      {
        name: 'DMS.INST.START_NODE',
        desc: 'DMS.INST.START_DESC',
        who: inst.startedBy ?? this.proc()?.startUserId ?? '',
        at: inst.startedAt as string | undefined,
        state: 'done' as 'done' | 'active' | 'waiting',
        note: 'DMS.INST.OC_COMPLETED'
      },
      ...inst.history.map((h) => ({ name: h.name, desc: h.comment ?? '', who: h.by ?? '', at: h.at, state: 'done' as const, note: note(h.outcome) }))
    ];
    if (running) {
      const step = planned[inst.currentStep];
      rows.push({
        name: step?.name ?? this.activeTask()?.name ?? '',
        desc: '',
        who: this.activeTask()?.assignee || step?.assigneeLabel || step?.assignee || '',
        at: this.activeTask()?.startedAt,
        state: 'active',
        note: ''
      });
      // only the steps this flow's values will actually reach
      for (const later of planned.slice(inst.currentStep + 1).filter((s) => this.wf.stepRunnable(s, inst.vars))) {
        rows.push({ name: later.name, desc: '', who: later.assigneeLabel || later.assignee || '', at: undefined, state: 'waiting', note: '' });
      }
    }
    rows.push({
      name: 'DMS.INST.END_NODE',
      desc: 'DMS.INST.END_DESC',
      who: '—',
      at: running ? undefined : inst.history[inst.history.length - 1]?.at,
      state: running ? 'waiting' : 'done',
      note: inst.status === 'cancelled' ? 'DMS.INST.OC_CANCELLED' : ''
    });
    return rows;
  }

  /** Only the person who started it may stop it — Alfresco enforces the same. */
  canCancel(): boolean {
    const me = this.me();
    return !!me && this.statusKey() === 'running' && (me === this.proc()?.startUserId || me === this.instance()?.startedBy);
  }

  /** Stops the flow: deletes the process now running and marks a template flow cancelled. */
  cancel(): void {
    const inst = this.instance();
    const processId = inst?.processId ?? this.proc()?.id;
    if (!processId) {
      return;
    }
    this.dialog
      .open(ConfirmDialogComponent, {
        data: { title: 'DMS.INST.CANCEL_TITLE', message: 'DMS.INST.CANCEL_MSG', yesLabel: 'DMS.INST.CANCEL_YES', noLabel: 'DMS.WF_DIALOG.CANCEL' },
        minWidth: '320px',
        restoreFocus: true
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed !== true) {
          return;
        }
        this.cancelling.set(true);
        this.wf
          .deleteProcess(processId)
          .pipe(switchMap(() => (inst ? this.wf.updateInstance({ ...inst, status: 'cancelled', taskId: undefined }) : of(null))))
          .subscribe({
            next: (saved) => {
              this.cancelling.set(false);
              if (saved) {
                this.instance.set(saved);
              }
              this.notification.showInfo(this.translate.instant('DMS.INST.CANCELLED'));
              this.load(this.proc()?.id ?? processId);
            },
            error: () => {
              this.cancelling.set(false);
              // a later step started by a reviewer is theirs in Alfresco's eyes
              this.notification.showError(this.translate.instant('DMS.INST.CANCEL_FAILED'));
            }
          });
      });
  }

  wfName(): string {
    const raw = String(this.proc()?.processDefinitionId ?? this.proc()?.processDefinitionKey ?? '').split(':')[0];
    return raw ? raw.replace(/([a-z])([A-Z])/g, '$1 $2') : '—';
  }

  wfVersion(): string {
    const parts = String(this.proc()?.processDefinitionId ?? '').split(':');
    return parts[1] ?? '1';
  }

  statusKey(): string {
    const p = this.proc();
    if (!p) {
      return 'unknown';
    }
    // a template flow outlives the process this page was opened on
    const inst = this.instance();
    if (inst) {
      return inst.status === 'returned' ? 'rejected' : inst.status;
    }
    if (p.completed || p.endedAt) {
      return this.variables()['wf_reviewOutcome'] === 'Reject' ? 'rejected' : 'completed';
    }
    return 'running';
  }

  /** How long the instance has been alive, as the "1 วัน 5 ชั่วโมง 20 นาที" parts. */
  elapsed(): { p0: number; p1: number; p2: number } {
    const start = this.proc()?.startedAt;
    const end = this.proc()?.endedAt;
    const ms = start ? (end ? new Date(end).getTime() : Date.now()) - new Date(start).getTime() : 0;
    return { p0: Math.floor(ms / 86400000), p1: Math.floor((ms % 86400000) / 3600000), p2: Math.floor((ms % 3600000) / 60000) };
  }

  /** The flow name the header/summary print — the template's when it has one. */
  flowName(): string {
    return this.instance()?.templateName || this.wfName();
  }

  /** The description line under the instance id. */
  flowDesc(): string {
    return String(this.variables()['bpm_workflowDescription'] ?? '');
  }

  /** Current assignee for the summary card. */
  currentAssignee(): string {
    return this.activeTask()?.assignee || '—';
  }

  fileClass(name?: string): string {
    return fileKindClass(name);
  }

  fileIcon(name?: string): string {
    return fileKindIcon(name);
  }

  /** Byte count in the shape the document cards print it. */
  sizeText(bytes?: number): string {
    if (!bytes || bytes < 1) {
      return '—';
    }
    if (bytes < 1024) {
      return bytes + ' B';
    }
    if (bytes < 1024 * 1024) {
      return (bytes / 1024).toFixed(1) + ' KB';
    }
    return (bytes / 1024 / 1024).toFixed(2) + ' MB';
  }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      this.loading.set(false);
      return;
    }
    this.wf
      .currentUserId()
      .pipe(catchError(() => of('')))
      .subscribe((me) => this.me.set(me));
    this.load(id);
    // someone else's decision lands without a reload — only while the flow
    // runs and the tab is in front, so a page left open costs nothing
    interval(InstanceDetailComponent.REFRESH_MS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.statusKey() === 'running' && !document.hidden && !this.loading()) {
          this.load(id, true);
        }
      });
  }

  /** `quiet` re-reads in place, without the progress bar. */
  private load(id: string, quiet = false): void {
    if (!quiet) {
      this.loading.set(true);
    }
    forkJoin({
      proc: this.wf.getProcess(id).pipe(catchError(() => of(undefined))),
      tasks: this.wf.processTasksAll(id),
      items: this.wf.processItems(id),
      vars: this.wf.processVariables(id),
      instance: this.wf.instanceForProcess(id),
      // warm the shared template catalogue so plannedSteps resolves real flows
      templates: this.wf.ensureTemplatesLoaded().pipe(catchError(() => of([])))
    }).subscribe(({ proc, tasks, items, vars, instance }) => {
      this.proc.set(proc);
      this.tasks.set(tasks);
      this.items.set(items);
      this.variables.set(vars);
      this.instance.set(instance);
      this.loading.set(false);
    });
  }

  openDoc(item: WorkflowItem): void {
    void this.router.navigate(['/document', item.id]);
  }

  openTask(taskId?: string): void {
    if (taskId) {
      void this.router.navigate(['/my-work/task', taskId]);
    }
  }

  /** Variable label without the bpm_/wf_ prefix noise. */
  varName(key: string): string {
    return key.replace(/^(bpm_|wf_|dms_)/, '').replace(/([a-z])([A-Z])/g, '$1 $2');
  }
}
